import { observer } from "mobx-react";
import React from "react";

import { ElementModel, IframeModel, ImageModel } from "@courses-pm2/api";

import { colors } from "@apps/viewer/src/ts/config";
import { Store } from "@apps/viewer/src/ts/store";

import { Lightbox } from "@apps/viewer/src/ts/components/utils/Lightbox/Lightbox";

type ElementLightboxProps = {
  /**
   * Contains a reference to the application lifecycle store, so we can
   * properly determine when to open an element in the lightbox.
   */
  store: Store;
};

/**
 * Utility that'll make sure the currently selected lightbox content will be
 * displayed.
 */
@observer
class ElementLightboxUnwrapped extends React.Component<ElementLightboxProps> {
  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    const { store } = this.props;

    const desiredDimensions = store.lightboxContent
      ? this.calculateSize(store.lightboxContent) || {}
      : {};

    return (
      <Lightbox onClose={this.onClose} active={!!store.lightboxContent}>
        <div
          className="wrapper"
          style={{
            height: desiredDimensions.height,
            width: desiredDimensions.width,
          }}
        >
          {store.lightboxContent &&
            store.engine.render(store.lightboxContent, { inLightbox: true })}
        </div>

        <button className="close" onClick={this.onClose} />

        <style jsx>{`
          .wrapper {
            /** Extend to make the most of the viewport */
            max-width: calc(100vw - 100px);
            max-height: calc(100vh - 100px);

            /** Handle scrolling within */
            overflow: auto;
            -webkit-overflow-scrolling: touch;

            /** Apply custom scrollbar styling */
            & ::-webkit-scrollbar {
              width: 10px;
            }

            & ::-webkit-scrollbar-track {
              background: #38383819;
            }

            & ::-webkit-scrollbar-thumb {
              background: #383838;
            }

            /** Prevent inline margins on first and last element */
            & > :global(:first-child) {
              margin-top: 0;
            }

            & > :global(:last-child) {
              margin-bottom: 0;
            }
          }

          .close {
            /** Position at the top-right corner */
            position: absolute;
            top: -15px;
            right: -15px;
            z-index: 10;

            /** Set up sizing */
            width: 30px;
            height: 30px;
            padding: 0;

            /** Apply box styling */
            -webkit-appearance: none;
            background: ${colors.black};
            border: none;
            border-radius: 50%;

            /** Indicate that element is clickable */
            cursor: pointer;

            /** Apply hover styling */
            transition: background-color 0.1s linear;

            &:hover {
              background-color: ${colors.orange};
            }

            /** Render cross using pseudo elements */
            &::before,
            &::after {
              /** Position the line */
              position: absolute;
              top: 7px;
              left: 14px;

              /** Set up sizing of the line */
              width: 2px;
              height: 16px;

              /** Render using background color */
              background: ${colors.white};

              /** Force rendering of pseudo-element */
              content: "";
            }

            &::before {
              transform: rotate(45deg);
            }

            &::after {
              transform: rotate(-45deg);
            }
          }
        `}</style>
      </Lightbox>
    );
  }

  /**
   * Helper that'll perform a qualified calculation of the desired sized of
   * the lightbox based on the available dimensions and the data being
   * rendered.
   */
  protected calculateSize(
    model: ElementModel,
  ): { width?: string; height?: string } | undefined {
    //
    // ImageComponent
    //
    if (model instanceof ImageModel) {
      // Bail out dimensions are unknown at mount time
      if (model.width === undefined || model.height === undefined) {
        return undefined;
      }

      // Calculate maximum allowed size of the image itself (taking into
      // account that there just might be both a top and bottom bar
      // around it)
      const maxWidth = window.innerWidth - 100;
      const maxHeight = window.innerHeight - 200;

      // Determine the desired dimensions of the image based on these
      // calculations, so that we can properly qualify the desired width
      let desiredWidth = Math.min(
        maxWidth,
        Math.round((maxHeight / model.height) * model.width),
      );

      // If scaling has been disabled for this image, then don't attempt
      // to up-scale it
      if (!model.enableScaleUp) {
        desiredWidth = Math.min(desiredWidth, model.width);
      }

      return { width: `${desiredWidth}px` };
    }

    //
    // IframeComponent
    //
    if (model instanceof IframeModel) {
      // Calculate maximum allowed size of the iframe itself (taking into
      // account that there just might be both a top and bottom bar
      // around it)
      const maxWidth = window.innerWidth - 100;
      const maxHeight = window.innerHeight - 200;

      // Determine the desired dimensions of the iframe based on these
      // calculations, so that we can properly qualify the desired width
      const desiredWidth = Math.min(
        maxWidth,
        Math.round(
          (maxHeight / model.aspectRatio.height) * model.aspectRatio.width,
        ),
      );

      // Return dimensions that'll utilize screen size to the fullest
      return { width: `${desiredWidth}px` };
    }

    // If we get here, there is no way to estimate the size of the
    // content, so bail out...
    return undefined;
  }

  /**
   * Reset data about selected element when the user wants to close the
   * lightbox.
   */
  protected onClose = () => {
    this.props.store.lightboxContent = undefined;
  };
}

export const ElementLightbox = ElementLightboxUnwrapped;
